import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoginHeader from './components/LoginHeader';
import SocialLoginButtons from './components/SocialLoginButtons';
import LoginForm from './components/LoginForm';
import SecurityBadges from './components/SecurityBadges';
import TOTPVerificationStep from '../../components/auth/TOTPVerificationStep';
import authService from '../../utils/authService';

const UserLogin = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTOTPVerification, setShowTOTPVerification] = useState(false);
  const [totpFactorId, setTotpFactorId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signInWithOAuth, user, authError } = useAuth();

  // Get return path from state
  const returnTo = location.state?.returnTo;
  const gameTitle = location.state?.gameTitle;

  useEffect(() => {
    // Check if user is already logged in
    if (user) {
      // If there's a return path, go there, otherwise go to dashboard
      navigate(returnTo || '/game-library-dashboard');
    }
  }, [user, navigate, returnTo]);

  // Update local error when auth error changes
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  const handleLogin = async (formData) => {
    setIsLoading(true);
    setError('');

    try {
      const result = await signIn(formData.email, formData.password);

      if (result.success) {
        // Save remember me preference
        if (formData.rememberMe) {
          localStorage.setItem('gamestream_remember', 'true');
        }

        // Check if user has MFA enabled and needs to verify
        const aal = await authService.getAuthenticatorAssuranceLevel();
        console.log('AAL Check:', aal);

        if (aal.success && aal.data.needsMFA) {
          // User has MFA enabled, need to verify TOTP
          const factors = await authService.listMFAFactors();
          console.log('MFA Factors:', factors);

          if (factors.success && factors.data.totp && factors.data.totp.length > 0) {
            console.log('Showing TOTP verification for factor:', factors.data.totp[0].id);
            setTotpFactorId(factors.data.totp[0].id);
            setShowTOTPVerification(true);
            setIsLoading(false);
            return;
          }
        } else {
          console.log('MFA not needed or AAL check failed');
        }

        // No MFA or already verified, navigate to return path or dashboard
        navigate(returnTo || '/game-library-dashboard');
      } else {
        setError(result.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
      console.log('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTOTPSuccess = () => {
    // TOTP verified, navigate to destination
    navigate(returnTo || '/game-library-dashboard');
  };

  const handleBackToLogin = () => {
    setShowTOTPVerification(false);
    setTotpFactorId(null);
  };

  const handleSocialLogin = async (provider) => {
    setIsLoading(true);
    setError('');

    try {
      const result = await signInWithOAuth(provider.toLowerCase());
      
      if (result.success) {
        // OAuth will redirect, so we don't need to navigate here
        // The redirect will be handled by Supabase
      } else {
        setError(result.error || `${provider} login failed. Please try again.`);
      }
    } catch (err) {
      setError(`${provider} login failed. Please try again.`);
      console.log(`${provider} login error:`, err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center p-4">
      {/* Background Gaming Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-secondary/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-accent/5 rounded-full blur-2xl"></div>
      </div>

      <div className="relative w-full max-w-md">
        <div className="bg-card/50 backdrop-blur-sm border border-border rounded-2xl shadow-gaming-xl p-8">
          {showTOTPVerification ? (
            <TOTPVerificationStep
              factorId={totpFactorId}
              onSuccess={handleTOTPSuccess}
              onBack={handleBackToLogin}
            />
          ) : (
            <>
              <LoginHeader />

              <div className="space-y-6">
                <SocialLoginButtons
                  onGoogleLogin={() => handleSocialLogin('Google')}
                  onDiscordLogin={() => handleSocialLogin('Discord')}
                  onTwitchLogin={() => handleSocialLogin('Twitch')}
                  isLoading={isLoading}
                />

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-card px-4 text-muted-foreground">Or continue with email</span>
                  </div>
                </div>

                <LoginForm
                  onSubmit={handleLogin}
                  isLoading={isLoading}
                  error={error}
                />
              </div>

              <SecurityBadges />
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-xs text-muted-foreground">
            By signing in, you agree to our Terms of Service and Privacy Policy
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            © {new Date().getFullYear()} Solis Games. All rights reserved.
          </p>
        </div>
        
      </div>
    </div>
  );
};

export default UserLogin;
